import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import { useLazyGetEnergyAlertSettingsQuery, useSaveEnergyAlertConfigurationMutation } from "../../../../service/energy-alerts-service";
import { emptyEnergyAlertSettings } from "../models/energy-alert-settings-response";
import { getErrorMessage } from "../../../../utils/error-handler";
import {
	trackAutoRechargeDisabled,
	trackAutoRechargeEnabled,
	trackLowThresholdReminderDisabled,
	trackLowThresholdReminderEnabled,
} from "../../../../services/business-analytics/energyManagementEvents";
import { ThresholdCard } from "./components/ThresholdCard";
import { AutoRechargeCard } from "./components/AutoRechargeCard";
import unitTransferGif from "../../../../assets/gifs/unit_transfer.gif";
import styles from "./unit-threshold-setup.module.scss";

export const UnitThresholdSetupPage = () => {
	const navigate = useNavigate();
	const [fetchSettings, { data: settings, isSuccess, isError }] = useLazyGetEnergyAlertSettingsQuery();

	useEffect(() => {
		fetchSettings();
	}, [fetchSettings]);
	const [saveConfiguration, { isLoading: isSavingThreshold }] = useSaveEnergyAlertConfigurationMutation();
	const [saveAutoRecharge, { isLoading: isSavingAutoRecharge }] = useSaveEnergyAlertConfigurationMutation();

	const effectiveSettings = settings ?? emptyEnergyAlertSettings;

	const [isEnabled, setIsEnabled] = useState(false);
	const [reminderOn, setReminderOn] = useState(false);
	const [thresholdInput, setThresholdInput] = useState<number | null>(null);
	const [thresholdSaved, setThresholdSaved] = useState(false);

	const [autoRechargeOn, setAutoRechargeOn] = useState(false);
	const [rechargeAmountInput, setRechargeAmountInput] = useState<number | null>(null);
	const [autoRechargeSaved, setAutoRechargeSaved] = useState(false);
	const [paymentMethodId, setPaymentMethodId] = useState<string | null>(null);
	const [paymentMethodLabel, setPaymentMethodLabel] = useState<string | null>(null);
	const [paymentMethodSubtitle, setPaymentMethodSubtitle] = useState<string | null>(null);

	const [initialized, setInitialized] = useState(false);

	useEffect(() => {
		if ((isSuccess || isError) && !initialized) {
			setIsEnabled(effectiveSettings.isEnabled);
			setReminderOn(effectiveSettings.isReminderEnabled);
			setThresholdInput(effectiveSettings.unitThreshold);
			setThresholdSaved(effectiveSettings.unitThreshold != null);
			setAutoRechargeOn(effectiveSettings.isAutoRechargeEnabled);
			setRechargeAmountInput(effectiveSettings.autoRechargeAmount);
			setAutoRechargeSaved(effectiveSettings.autoRechargeAmount != null);
			setPaymentMethodId(effectiveSettings.savedCardId);
			setInitialized(true);
		}
	}, [effectiveSettings, isSuccess, isError, initialized]);

	const effectiveReminderOn = reminderOn;
	const effectiveAutoRechargeOn = reminderOn && autoRechargeOn;

	const handleBack = () => navigate(-1);

	const handleSaveThreshold = async () => {
		try {
			await saveConfiguration({
				isEnabled,
				isReminderEnabled: reminderOn,
				unitThreshold: thresholdInput ?? 0,
				isAutoRechargeEnabled: autoRechargeOn,
				autoRechargeAmount: effectiveSettings.autoRechargeAmount ?? 0,
				savedCardId: effectiveSettings.savedCardId,
			}).unwrap();
			// Switching the reminder on is local until this save, so this is where it becomes enabled.
			trackLowThresholdReminderEnabled(thresholdInput);
			setThresholdSaved(true);
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data ?? error));
		}
	};

	const handleSaveAutoRecharge = async () => {
		try {
			await saveAutoRecharge({
				isEnabled,
				isReminderEnabled: reminderOn,
				unitThreshold: effectiveSettings.unitThreshold ?? 0,
				isAutoRechargeEnabled: autoRechargeOn,
				autoRechargeAmount: rechargeAmountInput ?? 0,
				savedCardId: paymentMethodId,
			}).unwrap();
			// Switching auto-recharge on is local until this save, so this is where it becomes enabled.
			trackAutoRechargeEnabled(rechargeAmountInput);
			setAutoRechargeSaved(true);
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data ?? error));
		}
	};

	const handleToggleReminder = async (val: boolean) => {
		setReminderOn(val);
		setThresholdSaved(false);
		if (val) {
			setIsEnabled(true);
		} else {
			setAutoRechargeOn(false);
			setRechargeAmountInput(null);
			setPaymentMethodId(null);
			setPaymentMethodLabel(null);
			setPaymentMethodSubtitle(null);
			setAutoRechargeSaved(false);
			try {
				await saveConfiguration({
					isEnabled,
					isReminderEnabled: false,
					unitThreshold: thresholdInput ?? 0,
					isAutoRechargeEnabled: false,
					autoRechargeAmount: effectiveSettings.autoRechargeAmount ?? 0,
					savedCardId: effectiveSettings.savedCardId,
				}).unwrap();
				trackLowThresholdReminderDisabled();
				// The same save switches auto-recharge off; `autoRechargeOn` still holds the value from before the toggle.
				if (autoRechargeOn) trackAutoRechargeDisabled();
			} catch (error: any) {
				toast.error(getErrorMessage(error?.data ?? error));
			}
		}
	};

	const handleToggleAutoRecharge = async (val: boolean) => {
		setAutoRechargeOn(val);
		setAutoRechargeSaved(false);
		if (!val) {
			setRechargeAmountInput(null);
			setPaymentMethodId(null);
			setPaymentMethodLabel(null);
			setPaymentMethodSubtitle(null);
			try {
				await saveAutoRecharge({
					isEnabled,
					isReminderEnabled: reminderOn,
					unitThreshold: effectiveSettings.unitThreshold ?? 0,
					isAutoRechargeEnabled: false,
					autoRechargeAmount: 0,
					savedCardId: null,
				}).unwrap();
				trackAutoRechargeDisabled();
			} catch (error: any) {
				toast.error(getErrorMessage(error?.data ?? error));
			}
		}
	};

	const handleThresholdChange = (val: number) => {
		setThresholdInput(val);
		setThresholdSaved(false);
	};

	const handleAmountChange = (val: number) => {
		setRechargeAmountInput(val);
		setAutoRechargeSaved(false);
	};

	const handleSelectPaymentMethod = (id: string, label: string, subtitle: string) => {
		setPaymentMethodId(id);
		setPaymentMethodLabel(label);
		setPaymentMethodSubtitle(subtitle);
		setAutoRechargeSaved(false);
	};

	return (
		<>
			<CustomerPageLayout title="Unit Threshold Reminder" onBack={handleBack}>
				{initialized && (
					<div className={styles.page}>
						<div className={styles.heroWrapper}>
							<div className={styles.heroCircle}>
								<img src={unitTransferGif} alt="" className={styles.heroGif} />
							</div>
						</div>

						<ThresholdCard
							reminderOn={effectiveReminderOn}
							thresholdInput={thresholdInput}
							thresholdSaved={thresholdSaved}
							isSaving={isSavingThreshold}
							onToggleReminder={handleToggleReminder}
							onThresholdChange={handleThresholdChange}
							onSave={handleSaveThreshold}
						/>

						<AutoRechargeCard
							autoRechargeOn={effectiveAutoRechargeOn}
							rechargeAmountInput={rechargeAmountInput}
							autoRechargeSaved={autoRechargeSaved}
							paymentMethodId={paymentMethodId}
							paymentMethodLabel={paymentMethodLabel}
							paymentMethodSubtitle={paymentMethodSubtitle}
							isSaving={isSavingAutoRecharge}
							disabled={!effectiveReminderOn}
							onToggleAutoRecharge={handleToggleAutoRecharge}
							onAmountChange={handleAmountChange}
							onSelectPaymentMethod={handleSelectPaymentMethod}
							onSave={handleSaveAutoRecharge}
						/>
					</div>
				)}
			</CustomerPageLayout>

		</>
	);
};
